import { API_URL } from '../../config';
import { createAuthHeaders } from './auth';
import { triggerServerError } from '../ui/feedback/ServerErrorOverlay';

function isJsonContent(headers) {
  const contentType = headers?.['Content-Type'] || headers?.['content-type'];
  return typeof contentType === 'string' && contentType.includes('application/json');
}

async function parseJsonSafe(response) {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function apiRequest(path: string, options: any = {}) {
  const {
    auth = false,
    headers = {},
    body,
    ...rest
  } = options;

  const requestHeaders = auth ? createAuthHeaders(headers) : { ...headers };
  const requestOptions = {
    ...rest,
    headers: requestHeaders,
  };

  if (body !== undefined) {
    const shouldSerializeBody = body !== null
      && typeof body === 'object'
      && !(body instanceof FormData)
      && !isJsonContent(requestHeaders);

    requestOptions.body = shouldSerializeBody ? JSON.stringify(body) : body;

    if (shouldSerializeBody) {
      requestOptions.headers = {
        ...requestHeaders,
        'Content-Type': 'application/json',
      };
    }
  }

  const response = await fetch(`${API_URL}${path}`, requestOptions);

  if (response.status >= 500) {
    const cloned = response.clone();
    try {
      const payload = await cloned.json();
      triggerServerError(payload?.trace_code || null);
    } catch {
      triggerServerError(null);
    }
  }

  return response;
}

export async function apiJson(path: string, options: any = {}) {
  const response = await apiRequest(path, options);
  const payload = await parseJsonSafe(response);

  if (!response.ok) {
    const message = payload?.error || payload?.message || `Request failed (${response.status})`;
    const error: any = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
}

// XHR file uploads (multipart/form-data) can't go through apiRequest/apiJson, but should
// still surface the backend's actual error text rather than a bare status code — same
// {error: "..."} convention as apiJson above.
export function xhrErrorMessage(xhr: XMLHttpRequest): string {
  try {
    const payload = JSON.parse(xhr.responseText);
    if (payload?.error) return payload.error;
  } catch {
    // Response wasn't JSON (e.g. a proxy/gateway error page) — fall through.
  }
  return `Upload failed (${xhr.status})`;
}
